import React from 'react';
import classNames from 'classnames';
import { Icon, makeStyles, DialogContent, Typography, Container } from '~/lib/bn-material-ui';
import { useOrdersDialogDispatch, useOrdersDialogStateSelector } from '../context/orders-dialog-context';
import { SiteFeedOrder } from './site-feed-order-details';
import { BarnLevelDisplay, BIN_PROFILE_WIDTH_PX, BIN_PROFILE_HEIGHT_PX } from './barn-level-display';
import { OrdersDialogOrderForm } from './orders-dialog-order-form';
import { OrdersDialogPhysicalBins } from './orders-dialog-physical-bins';
import { OrdersDialogActionButtons } from './orders-dialog-action-buttons';
import { OrdersDialogIntegrationButtons } from './orders-dialog-integration-buttons';
import { OrdersDialogStateBanner } from './orders-dialog-state-banner';
import { OrdersDialogNote } from './orders-dialog-note';
import { BnTabs, BnTab } from '~/common/bn-tabs-v2';
import { BarnSuggestionSettings } from './orders-dialog-barn-suggestion-settings';
import MicrosoftFinanceAndOperationsState
  from '~/orders/integrations/microsoft-finance-and-operations/microsoft-finance-and-operations-sync-state';
import { useAccountFeatures } from '~/utility/BinSentry-ui-utility';
import { FasOrderState } from '~/orders/integrations/fas/fas-order-state';

export const ACTION_ROW_MOBILE_HEIGHT_REM = 4;

const useContentStyles = makeStyles(({ palette, breakpoints, shadows }) => ({
  dialogDivider: {
    background: palette.gray[20],
    minHeight: '0.5rem',
    borderTop: `${palette.gray[40]} 1px solid`,
    borderBottom: `${palette.gray[40]} 1px solid`,
    [breakpoints.down('sm')]: {
      minHeight: 'unset',
      borderTop: 'unset',
    },
  },
  dialogContent: {
    padding: 0,
  },
  orderForm: {
    paddingTop: '.5rem',
    paddingBottom: '.5rem',
  },
  titleRow: {
    paddingBottom: '.5rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  titleIcon: {
    fontSize: '1em',
    textAlign: 'center',
    marginBottom: '-0.1em',
  },
  typeStateDisplay: {
    [breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  contentRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: '2rem',
    '@media (max-width: 820px)': {
      flexWrap: 'wrap',
    },
  },
  actionRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: '1rem',
    justifyContent: 'space-between',
    marginTop: '.5rem',
    [breakpoints.down('sm')]: {
      position: 'fixed',
      bottom: '0',
      width: '100%',
      left: '0',
      background: 'white',
      padding: '0.75rem',
      height: `${ACTION_ROW_MOBILE_HEIGHT_REM}rem`,
      zIndex: '20',
      boxShadow: shadows[4],
      marginLeft: '0.125rem',
      marginRight: '0.125rem',
    },
  },
  integrationRow: {
    display: 'flex',
    flexDirection: 'row',
    marginTop: '0.5rem',
    justifyContent: 'flex-end',
  },
  barnLevelContainer: {
    marginRight: '3.5em',
  },
}));

export function OrdersDialogContent() {
  const styles = useContentStyles();
  const [currentTab, selectedOrderId, selectedBarnId, initializationState] = useOrdersDialogStateSelector(['currentTab', 'selectedOrderId', 'selectedBarnId', 'initializationState']);
  const { features } = useAccountFeatures({ organizationId: selectedBarnId });

  const hasOrderIntegrationUi = initializationState === 'complete' && (features?.microsoftFinanceAndOperations || features?.fasOrders);

  return (<>
    <div className={styles.dialogDivider}>
      <Tabs />
    </div>
    <DialogContent className={styles.dialogContent}>
      {currentTab === 'orders' ?
        (<>
          <OrdersDialogContentContainer>
            <TitleRow />
            <OrdersDialogStateBanner />
            <div className={styles.contentRow}>
              <div className={styles.barnLevelContainer}>
                <BarnLevelDisplay />
              </div>

              <OrdersDialogOrderForm />
              <OrdersDialogPhysicalBins profileWidthPx={BIN_PROFILE_WIDTH_PX + 8} profileHeightPx={BIN_PROFILE_HEIGHT_PX}/>
            </div>
            <OrdersDialogNote />
            <div className={styles.actionRow}>
              <OrdersDialogIntegrationButtons />
              <OrdersDialogActionButtons />
            </div>
            { hasOrderIntegrationUi && (
              <div className={styles.integrationRow}>
                {features?.microsoftFinanceAndOperations && <MicrosoftFinanceAndOperationsState orderHref={selectedOrderId} />}
                {features?.fasOrders && <FasOrderState orderHref={selectedOrderId} />}
              </div>
            )}
          </OrdersDialogContentContainer>
          <div className={styles.dialogDivider}/>
          <SiteFeedOrder />
        </>) :
        (<OrdersDialogContentContainer>
          <BarnSuggestionSettings />
        </OrdersDialogContentContainer>)
      }
    </DialogContent>
  </>);
}

function OrdersDialogContentContainer({ children }) {
  const styles = useContentStyles();
  return <Container className={styles.orderForm} maxWidth='xl'>{children}</Container>;
}

function TitleText () {
  const [specifyingNewOrder, selectedOrderEntity, selectedBarnOrdersEntity] = useOrdersDialogStateSelector([
    'specifyingNewOrder',
    'selectedOrderEntity',
    'selectedBarnOrdersEntity',
  ]);

  if (!selectedBarnOrdersEntity && !selectedOrderEntity) {
    return null;
  }
  let verb;
  if (specifyingNewOrder) {
    verb = selectedBarnOrdersEntity?.hasAction('create-order') ? 'Create' : 'Request';
  } else {
    verb = selectedOrderEntity?.hasAction('update-order') ? 'Edit' : 'View';
  }

  return <Typography variant='h3'>{`${verb} Order`}</Typography>;
}

function TitleRow() {
  const styles = useContentStyles();

  return (
    <div className={styles.titleRow}>
      <TitleText />
      <TypeStateDisplay />
    </div>
  );
}

function TypeStateDisplay() {
  const styles = useContentStyles();
  const [selectedOrderEntity] = useOrdersDialogStateSelector([
    'selectedOrderEntity',
  ]);

  if (selectedOrderEntity) {
    if (selectedOrderEntity.properties.type === 'request') {
      return (
        <Typography className={styles.typeStateDisplay} variant='body1'>
          <Icon className={classNames('fas', 'fa-exclamation', styles.titleIcon)} /> Producer Req.
        </Typography>
      );
    }
    if (selectedOrderEntity.properties.type === 'suggestion') {
      return (
        <Typography className={styles.typeStateDisplay} variant='body1'>
          <Icon className={classNames('fas', 'fa-sparkles', styles.titleIcon)} /> Suggested
        </Typography>
      );
    }
  }

  return <Typography className={styles.typeStateDisplay} variant='body1'>Manual Input</Typography>;
}

function Tabs() {
  const [currentTab, hasSuggestions] = useOrdersDialogStateSelector(['currentTab', 'hasSuggestions']);
  const dispatch = useOrdersDialogDispatch();
  if (hasSuggestions !== true) {
    return null;
  }

  return (
    <BnTabs value={currentTab} onChange={(e, newTab) => dispatch({ type: 'change-tab', newTab })}>
      <BnTab label='Orders' value='orders'/>
      <BnTab label='Suggestion Settings' value='suggestion-settings'/>
    </BnTabs>
  );
}
